import React, { useState, useRef, useEffect } from 'react';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { CheckCircle2, XCircle, Sprout, RefreshCw, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent } from '@/lib/tracking';

const VegetationDensityPage: React.FC = () => {
  const [targetDensity, setTargetDensity] = useState([70]);
  const [nativeDensity, setNativeDensity] = useState([100]); 
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const appSignUpUrl = getSignupUrl();

  const requiredDensity = nativeDensity[0] * 0.7;
  const isCompliant = targetDensity[0] >= requiredDensity;
  
  const statusColor = isCompliant ? "text-green-600" : "text-red-600";
  const statusBg = isCompliant ? "bg-green-100 dark:bg-green-900/30" : "bg-red-100 dark:bg-red-900/30";
  const StatusIcon = isCompliant ? CheckCircle2 : XCircle;

  const drawVegetation = (density: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    
    ctx.fillStyle = '#d6d3d1'; 
    ctx.fillRect(0, 0, width, height);
    
    for (let i = 0; i < 5000; i++) {
        ctx.fillStyle = Math.random() > 0.5 ? '#a8a29e' : '#e7e5e4';
        ctx.fillRect(Math.random() * width, Math.random() * height, 2, 2);
    }

    const maxBlades = 15000;
    const bladeCount = (density / 100) * maxBlades;

    ctx.lineWidth = 1.5;

    for (let i = 0; i < bladeCount; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        const length = Math.random() * 8 + 4; 
        const angle = (Math.random() - 0.5) * 0.5; 

        ctx.strokeStyle = Math.random() > 0.5 ? '#16a34a' : '#15803d'; 
        
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + Math.sin(angle) * 5, y - length);
        ctx.stroke();
    }
  };

  useEffect(() => {
      drawVegetation(targetDensity[0]);
  }, [targetDensity]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "70% Vegetative Cover Visualizer",
    "description": "Visual tool to estimate vegetation density for EPA SWPPP Final Stabilization requirements.",
    "applicationCategory": "UtilitiesApplication",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-green-500/20">
      <SEO 
        title="70% Vegetative Cover Visualizer - Final Stabilization Guide"
        description="Visual guide to the EPA's 70% native background density requirement for final stabilization and permit termination."
        canonical="/resources/vegetation-density"
        jsonLd={jsonLd}
      />

      <div className="bg-muted/30 py-20 border-b border-border">
        <div className="container px-4 text-center max-w-4xl mx-auto">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: 'Stabilization Guide', href: '/resources/vegetation-density' }
           ]} />

           <div className="inline-flex items-center justify-center p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full mb-6">
             <Sprout size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
             The "70% Rule" Visualizer
           </h1>
           <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
             <strong>Chapter 9</strong> of the EPA Guide requires "70% of native background density" for final stabilization.
           </p>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-start mb-24">
            <Card className="border-border shadow-md order-2 md:order-1">
                <CardHeader>
                    <CardTitle className="flex justify-between items-center">
                        <span>Current Coverage</span>
                        <span className={`text-2xl font-bold ${statusColor}`}>{targetDensity[0]}%</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium">Your Site Density</label>
                        </div>
                        <Slider 
                            value={targetDensity} 
                            onValueChange={setTargetDensity} 
                            min={0} 
                            max={100} 
                            step={1} 
                        />
                    </div>
                    
                    <div className="pt-4 border-t">
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium text-muted-foreground">Native Background Density (Advanced)</label>
                            <span className="text-sm text-muted-foreground">{nativeDensity[0]}%</span>
                        </div>
                        <Slider 
                            value={nativeDensity} 
                            onValueChange={setNativeDensity} 
                            min={10} 
                            max={100} 
                            step={5}
                            className="opacity-50 hover:opacity-100 transition-opacity" 
                        />
                    </div>

                    <div className={`p-6 rounded-xl border flex items-start gap-4 ${statusBg} border-transparent`}>
                        <StatusIcon size={24} className={`shrink-0 ${statusColor}`} />
                        <div>
                            <h4 className={`font-bold text-lg mb-1 ${statusColor}`}>
                                {isCompliant ? "Pass: Stabilized" : "Fail: Not Stabilized"}
                            </h4>
                            <p className="text-sm text-foreground/80">
                                {isCompliant 
                                    ? `Vegetation meets the 70% requirement (${requiredDensity.toFixed(0)}% needed).`
                                    : `Coverage is insufficient. You need at least ${requiredDensity.toFixed(0)}% density.`
                                }
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="space-y-6 order-1 md:order-2">
               <div className="aspect-square rounded-2xl border-4 border-dashed border-stone-200 dark:border-stone-800 overflow-hidden shadow-inner relative bg-stone-100">
                   <canvas 
                        ref={canvasRef} 
                        width={600} 
                        height={600} 
                        className="w-full h-full object-cover"
                   />
                   <div className="absolute top-4 left-4 bg-white/90 dark:bg-black/80 backdrop-blur px-3 py-1 rounded text-xs font-bold shadow-sm border border-stone-200">
                       Simulation: {targetDensity[0]}% Coverage
                   </div>
                   <Button 
                        size="icon" 
                        variant="secondary" 
                        className="absolute bottom-4 right-4 rounded-full shadow-md"
                        onClick={() => drawVegetation(targetDensity[0])}
                        title="Regenerate Pattern"
                    >
                       <RefreshCw size={16} />
                   </Button>
               </div>
            </div>
        </div>

        {/* SEO CONTENT SECTION */}
        <div className="prose dark:prose-invert max-w-3xl mx-auto pt-8 border-t border-border">
            <h2>What is Final Stabilization?</h2>
            <p>
                Before you can file a <strong>Notice of Termination (NOT)</strong> to end your SWPPP coverage (and stop paying permit fees), you must achieve "Final Stabilization."
            </p>
            <p>
                The EPA defines this as achieving a uniform perennial vegetative cover with a density of <strong>70% of the native background vegetative cover</strong> for the area.
            </p>

            <h3>The "Native Background" Nuance</h3>
            <p>
                Many contractors mistakenly think they need 70% <em>total</em> coverage (like a lush lawn). This is incorrect. You need 70% of what <em>naturally grows there</em>.
            </p>
            <ul>
                <li><strong>Example 1 (Lush Area):</strong> If the undisturbed land next door has 100% grass cover, you need 70% cover to close your permit.</li>
                <li><strong>Example 2 (Arid Area):</strong> If you are building in the desert where natural vegetation is only 40% dense, you only need to achieve <strong>28% density</strong> (0.70 Ã— 40%) to be compliant.</li>
            </ul>

            <h3>How to document this?</h3>
            <p>
                When you submit your NOT, you should include:
            </p>
            <ol>
                <li>Photos of the stabilized area.</li>
                <li>Photos of the adjacent "native background" area for comparison.</li>
                <li>A brief narrative explaining your calculation.</li>
            </ol>
            
            <div className="not-prose mt-8 p-6 bg-primary/5 rounded-xl border border-primary/10">
                <h4 className="font-bold text-lg mb-2 text-foreground">Need help tracking stabilization?</h4>
                <p className="text-muted-foreground mb-4">
                    RunOffTrack allows you to log "Percent Stabilized" in your weekly inspection reports, creating a timeline of growth to prove compliance when you file your NOT.
                </p>
                <Button variant="link" className="p-0 h-auto text-primary" asChild>
                    <a href={appSignUpUrl} onClick={() => trackSignupIntent('vegetation_cta')}>Start Tracking Vegetation &rarr;</a>
                </Button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default VegetationDensityPage;
